import type { MatrixClient } from 'matrix-js-sdk'
import { MATRIX_HOMESERVER } from '@/modules/chat-matrix-fast/config'

export interface MatrixUserContext {
  employeeId?: string
  matrixUserId?: string
  logtoSub?: string
  phoneNumber?: string | null
}

function getMatrixServerName(): string {
  return new URL(MATRIX_HOMESERVER).hostname
}

function withServer(mxidOrLocalpart: string): string {
  if (!mxidOrLocalpart.startsWith('@')) {
    return `@${mxidOrLocalpart}:${getMatrixServerName()}`
  }

  return mxidOrLocalpart.includes(':')
    ? mxidOrLocalpart
    : `${mxidOrLocalpart}:${getMatrixServerName()}`
}

/** Нормализует телефон к формату Matrix localpart: 79XXXXXXXXX */
export function normalizePhoneForMatrix(phone: string): string {
  const digits = phone.replace(/\D/g, '')

  if (digits.length === 10 && digits.startsWith('9')) {
    return `7${digits}`
  }

  if (digits.length === 11 && digits.startsWith('8')) {
    return `7${digits.slice(1)}`
  }

  return digits
}

/** Человекочитаемое имя из MXID, если display name не задан в Matrix */
export function formatMatrixDisplayName(mxidOrName: string): string {
  const value = String(mxidOrName || '').trim()
  if (!value.startsWith('@')) {
    return value
  }

  const localpart = value.split(':')[0].slice(1)
  const phoneMatch = /^u(\d{10,15})$/.exec(localpart)
  if (!phoneMatch) {
    return localpart || value
  }

  const phone = phoneMatch[1]
  if (phone.length === 11 && phone.startsWith('7')) {
    return `+7 (${phone.slice(1, 4)}) ${phone.slice(4, 7)}-${phone.slice(7, 9)}-${phone.slice(9, 11)}`
  }

  return phone
}

function isPhoneLike(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  return digits.length >= 10 && digits.length <= 15
}

function matrixLocalpartFromPhone(phone: string): string {
  return `u${normalizePhoneForMatrix(phone)}`
}

export function phoneFromMatrixLocalpart(localpart: string): string | null {
  if (!/^u\d+$/.test(localpart)) {
    return null
  }

  return localpart.slice(1)
}

/** Извлекает телефон из mxid собеседника (@u79991234567:server), если он там закодирован */
export function phoneFromMatrixUserId(mxid: string | undefined | null): string | null {
  if (!mxid) {
    return null
  }

  const localpart = mxid.split(':')[0].replace(/^@/, '')
  return phoneFromMatrixLocalpart(localpart)
}

export function phoneToMatrixUserId(phone: string): string {
  return withServer(matrixLocalpartFromPhone(phone))
}

function buildMatrixUserIdCandidates(
  identifier: string | number,
  currentUser?: MatrixUserContext,
): string[] {
  const id = String(identifier).trim()
  const ordered: string[] = []

  const add = (value: string) => {
    const mxid = withServer(value)
    if (!ordered.includes(mxid)) {
      ordered.push(mxid)
    }
  }

  if (id.startsWith('@')) {
    add(id)
    return ordered
  }

  if (isPhoneLike(id)) {
    add(matrixLocalpartFromPhone(id))
  }

  if (currentUser?.matrixUserId) {
    const localpart = currentUser.matrixUserId.split(':')[0].slice(1)
    const currentPhoneDigits = currentUser.phoneNumber
      ? normalizePhoneForMatrix(currentUser.phoneNumber)
      : phoneFromMatrixLocalpart(localpart)

    if (currentPhoneDigits && localpart === `u${currentPhoneDigits}` && isPhoneLike(id)) {
      add(matrixLocalpartFromPhone(id))
    }
  }

  const idNoDashes = id.replace(/-/g, '')
  add(`u${id}`)
  add(`u${idNoDashes}`)
  add(id)
  add(idNoDashes)

  return ordered
}

async function matrixUserExists(matrixClient: MatrixClient, mxid: string): Promise<boolean> {
  try {
    await matrixClient.getProfileInfo(mxid)
    return true
  } catch {
    return false
  }
}

function inferMatrixUserId(
  identifier: string | number,
  currentUser?: MatrixUserContext,
): string | null {
  const id = String(identifier).trim()

  if (id.startsWith('@')) {
    return withServer(id)
  }

  if (isPhoneLike(id)) {
    return phoneToMatrixUserId(id)
  }

  if (!currentUser?.matrixUserId) {
    return null
  }

  const localpart = currentUser.matrixUserId.split(':')[0].slice(1)

  if (currentUser.logtoSub && localpart === currentUser.logtoSub) {
    return null
  }

  const currentPhoneDigits = currentUser.phoneNumber
    ? normalizePhoneForMatrix(currentUser.phoneNumber)
    : phoneFromMatrixLocalpart(localpart)

  if (currentPhoneDigits && localpart === `u${currentPhoneDigits}`) {
    return null
  }

  return null
}

export async function resolveMatrixUserId(
  matrixClient: MatrixClient,
  identifier: string | number,
  currentUser?: MatrixUserContext,
): Promise<string | null> {
  const id = String(identifier).trim()

  if (isPhoneLike(id)) {
    const mxid = phoneToMatrixUserId(id)
    if (await matrixUserExists(matrixClient, mxid)) {
      return mxid
    }

    return mxid
  }

  const candidates = buildMatrixUserIdCandidates(identifier, currentUser)

  for (const mxid of candidates) {
    if (await matrixUserExists(matrixClient, mxid)) {
      return mxid
    }
  }

  return inferMatrixUserId(identifier, currentUser)
}